/**
 * ProductMovementsModal — Fév 2026
 * Affiche l'historique complet des mouvements d'un produit (stock).
 * Récupère GET /api/stock/movements?product_id={id}
 *
 * Utilisation :
 *   <ProductMovementsModal
 *      open={!!historyProduct}
 *      onOpenChange={(v) => !v && setHistoryProduct(null)}
 *      product={historyProduct}
 *   />
 */
import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowDownCircle, ArrowUpCircle, RefreshCw, History, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (n) => (Number(n) || 0).toLocaleString("fr-FR");
const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

// Types de mouvements → étiquette + couleur + direction (+/-)
const TYPE_META = {
  entree:              { label: "Entrée",           color: "emerald", sign: "+" },
  sortie:              { label: "Sortie",           color: "rose",    sign: "-" },
  perte:               { label: "Perte",            color: "orange",  sign: "-" },
  casse:               { label: "Casse",            color: "orange",  sign: "-" },
  transfert_entree:    { label: "Transfert +",      color: "cyan",    sign: "+" },
  transfert_sortie:    { label: "Transfert -",      color: "cyan",    sign: "-" },
  destockage_recette:  { label: "Vente (déstock)",  color: "blue",    sign: "-" },
  destockage_caisse:   { label: "Vente caisse",     color: "blue",    sign: "-" },
  ajustement:          { label: "Ajustement",       color: "amber",   sign: "±" },
  compensation:        { label: "Compensation",     color: "violet",  sign: "±" },
  kitchen_cancel:      { label: "Annulation cuisine", color: "yellow", sign: "+" },
};

const typeMeta = (t) => TYPE_META[t] || { label: t || "?", color: "slate", sign: "" };

const badgeClass = (color) => {
  const map = {
    emerald: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    rose:    "bg-rose-500/20 text-rose-300 border-rose-500/40",
    orange:  "bg-orange-500/20 text-orange-300 border-orange-500/40",
    cyan:    "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
    blue:    "bg-blue-500/20 text-blue-300 border-blue-500/40",
    amber:   "bg-amber-500/20 text-amber-300 border-amber-500/40",
    violet:  "bg-violet-500/20 text-violet-300 border-violet-500/40",
    yellow:  "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
    slate:   "bg-slate-600/30 text-slate-300 border-slate-500/40",
  };
  return `border ${map[color] || map.slate} text-[10px] font-bold uppercase px-2 py-0.5`;
};

const ProductMovementsModal = ({ open, onOpenChange, product }) => {
  const [loading, setLoading] = React.useState(false);
  const [movements, setMovements] = React.useState([]);
  const [error, setError] = React.useState("");

  const fetchMovements = React.useCallback(async () => {
    if (!product?.id) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/stock/movements`, {
        params: { product_id: product.id, limit: 500 },
      });
      setMovements(res?.data?.movements || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur";
      setError(String(msg));
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  }, [product?.id]);

  React.useEffect(() => {
    if (open) fetchMovements();
    else { setMovements([]); setError(""); }
  }, [open, fetchMovements]);

  // Ventilation par type (KPI)
  const summary = React.useMemo(() => {
    const acc = { count: movements.length, in: 0, out: 0, totalValue: 0 };
    for (const m of movements) {
      const meta = typeMeta(m.movement_type);
      const qty = Number(m.quantity) || 0;
      if (meta.sign === "+") acc.in += qty;
      else if (meta.sign === "-") acc.out += qty;
      acc.totalValue += Number(m.total_value) || 0;
    }
    return acc;
  }, [movements]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-900 border-slate-700 text-white sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="product-movements-modal"
      >
        <DialogHeader className="p-4 pb-3 border-b border-slate-800 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-white pr-8">
            <History className="w-5 h-5 text-blue-400" strokeWidth={2.2} />
            Historique des mouvements
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            {product?.name}{product?.code ? ` · ${product.code}` : ""} · stock actuel : <span className="text-white font-semibold">{formatPrice(product?.quantity)} {product?.unit || ""}</span>
          </DialogDescription>
        </DialogHeader>

        {/* KPI */}
        {!loading && movements.length > 0 && (
          <div className="grid grid-cols-3 gap-2 px-4 pt-3 flex-shrink-0" data-testid="product-movements-kpi">
            <div className="bg-slate-800/60 border border-slate-700 rounded p-2 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Total mouv.</p>
              <p className="text-lg font-bold text-white">{summary.count}</p>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-center">
              <p className="text-[10px] text-emerald-300 uppercase tracking-wider">Entrées</p>
              <p className="text-lg font-bold text-emerald-200">+{formatPrice(summary.in)}</p>
            </div>
            <div className="bg-rose-500/10 border border-rose-500/30 rounded p-2 text-center">
              <p className="text-[10px] text-rose-300 uppercase tracking-wider">Sorties</p>
              <p className="text-lg font-bold text-rose-200">-{formatPrice(summary.out)}</p>
            </div>
          </div>
        )}

        {/* Corps */}
        <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Chargement des mouvements…
            </div>
          ) : error ? (
            <div className="text-center py-8 text-rose-400 text-sm">{error}</div>
          ) : movements.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <History className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Aucun mouvement enregistré pour ce produit.</p>
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="product-movements-list">
              {movements.map((m, idx) => {
                const meta = typeMeta(m.movement_type);
                const qty = Number(m.quantity) || 0;
                const sign = meta.sign === "-" ? "-" : (meta.sign === "+" ? "+" : "");
                const Icon = meta.sign === "+" ? ArrowDownCircle : (meta.sign === "-" ? ArrowUpCircle : RefreshCw);
                return (
                  <div
                    key={m.id || idx}
                    className="bg-slate-800/50 hover:bg-slate-800/80 border border-slate-700/60 rounded-md p-2.5 flex items-start gap-3 transition-colors"
                    data-testid={`movement-row-${m.id || idx}`}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${meta.sign === "+" ? "text-emerald-400" : meta.sign === "-" ? "text-rose-400" : "text-amber-400"}`} strokeWidth={2.4} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={badgeClass(meta.color)}>{meta.label}</Badge>
                        <span className={`text-sm font-bold tabular-nums ${meta.sign === "+" ? "text-emerald-300" : meta.sign === "-" ? "text-rose-300" : "text-amber-300"}`}>
                          {sign}{formatPrice(qty)} {m.unit || product?.unit || ""}
                        </span>
                        {m.previous_quantity !== undefined && m.new_quantity !== undefined && (
                          <span className="text-[10px] text-slate-500 font-mono">
                            {formatPrice(m.previous_quantity)} → {formatPrice(m.new_quantity)}
                          </span>
                        )}
                        {(m.total_value || 0) > 0 && (
                          <span className="text-[10px] text-slate-400 font-mono ml-auto">
                            {formatPrice(m.total_value)} F
                          </span>
                        )}
                      </div>
                      {m.reason && (
                        <p className="text-xs text-slate-300 mt-0.5 truncate" title={m.reason}>{m.reason}</p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        <span className="text-[10px] text-slate-500">{formatDateTime(m.created_at)}</span>
                        {m.user_name && <span className="text-[10px] text-slate-400">· {m.user_name}</span>}
                        {m.source && <span className="text-[10px] text-slate-500 italic">· {m.source}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-800 flex justify-between items-center gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchMovements}
            disabled={loading}
            className="text-slate-300 hover:bg-slate-800"
            data-testid="product-movements-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="border-slate-600 text-slate-200 hover:bg-slate-800"
            data-testid="product-movements-close"
          >
            <X className="w-4 h-4 mr-1" />
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductMovementsModal;
